import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../components/ui/dialog';
import { 
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Phone, 
  Home, FileText, CheckCircle2, XCircle, Clock, AlertCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DebtTrackingPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [overdueSummary, setOverdueSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [contractDetail, setContractDetail] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchOverdueSummary();
  }, []);

  const fetchOverdueSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/overdue-summary`, { withCredentials: true });
      setOverdueSummary(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAccruals = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/contracts/generate-accruals`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchOverdueSummary();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setGenerating(false);
    }
  };

  const viewContractPayments = async (contractId) => {
    if (!contractId) return;
    try {
      const response = await axios.get(`${API_URL}/api/contracts/${contractId}/payment-status`, { withCredentials: true });
      setContractDetail(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case 'partial': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'overdue': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      paid: language === 'tr' ? 'Ödendi' : 'Paid',
      partial: language === 'tr' ? 'Kısmi' : 'Partial',
      overdue: language === 'tr' ? 'Gecikmiş' : 'Overdue',
      unpaid: language === 'tr' ? 'Bekliyor' : 'Pending'
    };
    return map[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
      case 'partial': return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'overdue': return 'bg-red-50 text-red-700 border-red-300';
      default: return 'bg-gray-50 text-gray-600 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  const tenants = overdueSummary?.tenants || [];
  const totalOverdue = overdueSummary?.total_overdue || 0;

  return (
    <div className="space-y-6" data-testid="debt-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('debtTracking')}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {tenants.length} {t('tenantsWithDebt')}
          </p>
        </div>
        <Button 
          onClick={handleGenerateAccruals}
          disabled={generating}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="generate-accruals-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
          {t('generateAccruals')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-red-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('totalDebt')}</p>
          <p className="font-heading text-3xl font-bold text-red-600" data-testid="total-debt-amount">
            {currency}{totalOverdue.toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('tenantsWithDebt')}</p>
          <p className="font-heading text-3xl font-bold">{tenants.length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('overdueCount')}</p>
          <p className="font-heading text-3xl font-bold text-amber-600">
            {tenants.reduce((sum, t) => sum + t.overdue_count, 0)}
          </p>
        </div>
      </div>

      {/* Tenant Debt List */}
      {tenants.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-gray-500">{t('noDebt')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tenants.map((tenant) => (
            <div key={tenant.tenant_id} className="bg-white border border-gray-200" data-testid={`debt-tenant-${tenant.tenant_id}`}>
              {/* Tenant Header Row */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedTenant(expandedTenant === tenant.tenant_id ? null : tenant.tenant_id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-100 text-red-700 flex items-center justify-center font-bold text-sm">
                    {tenant.tenant_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{tenant.tenant_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        <Home className="w-3 h-3" />
                        {tenant.unit_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {tenant.tenant_phone}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono text-lg font-bold text-red-600">{currency}{tenant.total_debt.toLocaleString()}</p>
                    <div className="flex items-center gap-2 text-xs mt-0.5">
                      {tenant.overdue_count > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-200">
                          {tenant.overdue_count} {t('overdueCount')}
                        </span>
                      )}
                      {tenant.unpaid_count > 0 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 border border-gray-200">
                          {tenant.unpaid_count} {t('unpaidCount')}
                        </span>
                      )}
                      {tenant.partial_count > 0 && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">
                          {tenant.partial_count} {t('partialCount')}
                        </span>
                      )}
                    </div>
                  </div>
                  {expandedTenant === tenant.tenant_id ? 
                    <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  }
                </div>
              </div>

              {/* Expanded Accrual Details */}
              {expandedTenant === tenant.tenant_id && (
                <div className="border-t border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                        <th className="px-4 py-2 text-left">{t('description')}</th>
                        <th className="px-4 py-2 text-left">{t('dueDate')}</th>
                        <th className="px-4 py-2 text-right">{t('amount')}</th>
                        <th className="px-4 py-2 text-right">{t('paidAmount')}</th>
                        <th className="px-4 py-2 text-right">{t('remainingAmount')}</th>
                        <th className="px-4 py-2 text-center">{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenant.accruals.sort((a, b) => a.due_date.localeCompare(b.due_date)).map((accrual) => (
                        <tr key={accrual.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium">
                            {accrual.description || accrual.accrual_type}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {accrual.due_date?.split('T')[0]}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono">
                            {currency}{accrual.amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-emerald-600">
                            {currency}{accrual.paid_amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-bold text-red-600">
                            {currency}{accrual.remaining_amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase border ${getStatusBadgeClass(accrual.status)}`}>
                              {getStatusIcon(accrual.status)}
                              {getStatusLabel(accrual.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tenant.contract_id && (
                    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewContractPayments(tenant.contract_id)}
                        className="rounded-none text-xs"
                        data-testid={`view-contract-detail-${tenant.tenant_id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {t('contractPayments')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Contract Payment Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {t('contractPayments')}
            </DialogTitle>
          </DialogHeader>
          {contractDetail && (
            <div className="space-y-4">
              {/* Contract Info */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 border border-gray-200 text-sm">
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('tenants')}</p>
                  <p className="font-medium">{contractDetail.tenant_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('units')}</p>
                  <p className="font-medium">{contractDetail.unit_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">{t('monthlyRent')}</p>
                  <p className="font-mono font-bold">{currency}{contractDetail.contract?.monthly_rent?.toLocaleString()}</p>
                </div>
              </div>

              {/* Monthly Payment Grid */}
              <div className="border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">{t('description')}</th>
                      <th className="px-4 py-2 text-left">{t('dueDate')}</th>
                      <th className="px-4 py-2 text-right">{t('amount')}</th>
                      <th className="px-4 py-2 text-right">{t('paidAmount')}</th>
                      <th className="px-4 py-2 text-right">{t('remainingAmount')}</th>
                      <th className="px-4 py-2 text-center">{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractDetail.accruals?.map((accrual) => (
                      <tr key={accrual.id} className="border-t border-gray-100">
                        <td className="px-4 py-2.5 font-medium">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(accrual.status)}
                            {accrual.description || accrual.accrual_type}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{accrual.due_date?.split('T')[0]}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{currency}{accrual.amount.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{currency}{accrual.paid_amount.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-red-600">
                          {accrual.remaining_amount > 0 ? `${currency}${accrual.remaining_amount.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase border ${getStatusBadgeClass(accrual.status)}`}>
                            {getStatusLabel(accrual.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              {contractDetail.accruals?.length > 0 && (
                <div className="flex justify-end gap-6 p-3 bg-gray-50 border border-gray-200 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase">{t('totalDebt')}</p>
                    <p className="font-mono font-bold text-red-600">
                      {currency}{contractDetail.accruals.reduce((s, a) => s + a.remaining_amount, 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase">{t('paidAmount')}</p>
                    <p className="font-mono font-bold text-emerald-600">
                      {currency}{contractDetail.accruals.reduce((s, a) => s + a.paid_amount, 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DebtTrackingPage;
